/**
 * Changes to a Studio board, as Ardi proposes them and as the board applies
 * them. Mirrored from pms-api-v2 `studio/studio-board-ops.ts` — keep the two
 * identical (the schema and validation are only used server-side).
 */
import {
    CAMERA_ANGLES, CAMERA_MOVES, STUDIO_FORMATS, STUDIO_LOOKS,
    StudioBoard, StudioShot,
} from './studio-contract';
import { calloutsToGraphics } from './graphics';

export type BoardOp =
    | { op: 'set'; format?: StudioBoard['format']; look?: StudioBoard['look']; brief?: string; musicPrompt?: string }
    | { op: 'add_shot'; shot: Partial<StudioShot>; after?: string }
    | { op: 'update_shot'; id: string; patch: Partial<StudioShot> }
    | { op: 'remove_shot'; id: string }
    | { op: 'reorder'; ids: string[] };

export interface BoardProposal {
    reply: string;
    /** A short name for a new production (3 to 5 words). */
    title?: string;
    ops: BoardOp[];
    render: 'stills' | 'clips' | 'none';
    /** Limit `render` to these shots; default = every shot the ops added or changed. */
    renderShotIds?: string[];
    suggestions?: string[];
}

const SHOT_PROPS = {
    id: { type: 'string', description: 'Keep existing ids; for a new shot you may omit it.' },
    title: { type: 'string', description: 'Two or three words, for the card.' },
    productUuids: { type: 'array', items: { type: 'string' }, maxItems: 4 },
    scene: { type: 'string' },
    camera: { type: 'string', enum: CAMERA_ANGLES },
    cameraNote: { type: 'string' },
    framingRef: {
        type: 'object',
        properties: { url: { type: 'string' }, source: { type: 'string', enum: ['reference', 'studio3d', 'upload'] } },
        required: ['url', 'source'],
        additionalProperties: false,
    },
    action: { type: 'string' },
    kind: { type: 'string', enum: ['image', 'video'] },
    motion: { type: 'string', enum: CAMERA_MOVES },
    durationSec: { type: 'number', minimum: 2, maximum: 10 },
    engine: { type: 'string', enum: ['cinematic', 'fast'] },
    text: {
        type: 'object',
        properties: {
            content: { type: 'string' },
            role: { type: 'string', enum: ['title', 'caption', 'label'] },
            position: { type: 'string', enum: ['top', 'center', 'bottom', 'bottom-right', 'bottom-left'] },
            font: { type: 'string', enum: ['serif', 'sans', 'brand'] },
            animation: { type: 'string', enum: ['none', 'fade', 'rise', 'type'] },
        },
        required: ['content', 'role'],
        additionalProperties: false,
    },
};

export const BOARD_PROPOSAL_SCHEMA = {
    type: 'object',
    required: ['reply', 'ops', 'render'],
    additionalProperties: false,
    properties: {
        reply: { type: 'string', description: '1–3 short sentences to the vendor, in their language.' },
        ops: {
            type: 'array',
            items: {
                type: 'object',
                required: ['op'],
                properties: {
                    op: { type: 'string', enum: ['set', 'add_shot', 'update_shot', 'remove_shot', 'reorder'] },
                    format: { type: 'string', enum: STUDIO_FORMATS },
                    look: { type: 'string', enum: STUDIO_LOOKS },
                    brief: { type: 'string' },
                    musicPrompt: { type: 'string', description: 'For the edit: mood/genre/tempo of the music bed.' },
                    shot: { type: 'object', properties: SHOT_PROPS, additionalProperties: false, description: 'add_shot: the new shot.' },
                    after: { type: 'string', description: 'add_shot: insert after this shot id (default: at the end).' },
                    id: { type: 'string', description: 'update_shot / remove_shot: the shot id.' },
                    patch: { type: 'object', properties: SHOT_PROPS, additionalProperties: false, description: 'update_shot: only the fields to change.' },
                    ids: { type: 'array', items: { type: 'string' }, description: 'reorder: every shot id in the new order.' },
                },
                additionalProperties: false,
            },
        },
        render: { type: 'string', enum: ['stills', 'clips', 'none'] },
        renderShotIds: { type: 'array', items: { type: 'string' } },
        suggestions: { type: 'array', items: { type: 'string' }, maxItems: 4 },
    },
};

let counter = 0;
export function newShotId(): string {
    counter = (counter + 1) % 1000;
    return `s${Date.now().toString(36)}${counter.toString(36)}`;
}

function cleanShot(s: Partial<StudioShot>, fallbackId?: string): StudioShot {
    const camera = CAMERA_ANGLES.includes(s.camera as any) ? s.camera! : 'three-quarter';
    // Mirrors the API: a card, its background, the backdrop of a 3D shot and the
    // vendor's recorded take all survive. Read as "image", an end card Ardi had
    // made came back as a photo of the room with a logo on it (13 Oct 2026).
    const kind = s.kind === 'video' ? 'video' : s.kind === 'card' ? 'card' : 'image';
    const background = typeof s.background === 'string' && s.background.trim() ? s.background.trim() : undefined;
    const take3d = s.take3d?.url && Number(s.take3d.durationSec) > 0 ? s.take3d : undefined;
    return {
        id: s.id || fallbackId || newShotId(),
        title: s.title,
        productUuids: Array.isArray(s.productUuids) ? s.productUuids.filter(Boolean).slice(0, 4) : [],
        scene: String(s.scene || '').trim(),
        camera,
        cameraNote: s.cameraNote,
        framingRef: s.framingRef?.url ? s.framingRef : undefined,
        action: s.action,
        kind,
        background: kind === 'card' ? background || '#0B0B0B' : undefined,
        motion: kind === 'video' ? (CAMERA_MOVES.includes(s.motion as any) ? s.motion : 'push-in') : s.motion,
        durationSec: kind === 'video' ? Math.max(0.5, Math.min(take3d ? 15 : 10, Number(s.durationSec) || 4))
            : kind === 'card' ? Math.max(0.5, Math.min(8, Number(s.durationSec) || 2)) : s.durationSec,
        engine: s.engine,
        take3d,
        text: s.text?.content ? s.text : undefined,
        graphics: Array.isArray(s.graphics) && s.graphics.length ? s.graphics.slice(0, 40) : calloutsToGraphics(s.callouts),
        transition: s.transition,
        basedOn: s.basedOn && s.basedOn !== s.id ? s.basedOn : undefined,
        inFilm: s.inFilm === false ? false : undefined,
        surfaceUse: s.surfaceUse === 'object' || s.surfaceUse === 'installed' ? s.surfaceUse : undefined,
        surfaceOn: s.surfaceOn === 'floor' || s.surfaceOn === 'walls' ? s.surfaceOn : undefined,
        lookRef: typeof s.lookRef?.url === 'string' && /^https?:/.test(s.lookRef.url) ? { url: s.lookRef.url } : undefined,
        pose: s.pose,
        move3d: s.move3d,
        backdrop: s.backdrop,
        still: s.still,
        clip: s.clip,
        design: s.design,
    };
}

/**
 * Apply ops to a board. Returns the new board and the ids of shots whose
 * picture is now out of date (added, or changed in a way that changes it).
 */
export function applyBoardOps(board: StudioBoard, ops: BoardOp[]): { board: StudioBoard; changed: string[] } {
    const next: StudioBoard = { ...board, shots: [...(board.shots || [])] };
    const changed = new Set<string>();
    const VISUAL = ['productUuids', 'scene', 'camera', 'cameraNote', 'framingRef', 'action'];
    for (const op of ops || []) {
        if (!op || typeof op !== 'object') continue;
        if (op.op === 'set') {
            // Models sometimes nest the fields in `patch` (the update_shot
            // shape); the meaning is unambiguous, so take it either way.
            const nested = (op as any).patch;
            const so = (nested && typeof nested === 'object' ? { ...nested, ...op } : op) as Extract<BoardOp, { op: 'set' }>;
            if (so.format && STUDIO_FORMATS.includes(so.format)) {
                if (so.format !== next.format) next.shots.forEach((s) => changed.add(s.id));
                next.format = so.format;
            }
            if (so.look && STUDIO_LOOKS.includes(so.look)) {
                if (so.look !== next.look) next.shots.forEach((s) => changed.add(s.id));
                next.look = so.look;
            }
            if (typeof so.brief === 'string') next.brief = so.brief;
            if (typeof so.musicPrompt === 'string') next.edit = { ...(next.edit || {}), musicPrompt: so.musicPrompt };
        } else if (op.op === 'add_shot' && op.shot) {
            const shot = cleanShot(op.shot);
            if (next.shots.some((s) => s.id === shot.id)) shot.id = newShotId();
            const at = op.after ? next.shots.findIndex((s) => s.id === op.after) : -1;
            if (at >= 0) next.shots.splice(at + 1, 0, shot); else next.shots.push(shot);
            changed.add(shot.id);
        } else if (op.op === 'update_shot' && op.id && op.patch) {
            const i = next.shots.findIndex((s) => s.id === op.id);
            if (i < 0) continue;
            const before = next.shots[i];
            const merged = cleanShot({ ...before, ...op.patch, id: before.id, still: before.still, clip: before.clip });
            next.shots[i] = merged;
            if (VISUAL.some((k) => JSON.stringify((before as any)[k]) !== JSON.stringify((merged as any)[k]))) changed.add(merged.id);
        } else if (op.op === 'remove_shot' && op.id) {
            next.shots = next.shots.filter((s) => s.id !== op.id);
            changed.delete(op.id);
        } else if (op.op === 'reorder' && Array.isArray(op.ids)) {
            const byId = new Map(next.shots.map((s) => [s.id, s]));
            const ordered = op.ids.map((id) => byId.get(id)).filter(Boolean) as StudioShot[];
            const rest = next.shots.filter((s) => !op.ids.includes(s.id));
            next.shots = [...ordered, ...rest];
        }
    }
    return { board: next, changed: [...changed].filter((id) => next.shots.some((s) => s.id === id)) };
}

/** Problems that would make a proposal unusable, for the model to fix. */
export function validateProposal(p: BoardProposal, knownProductUuids: Set<string>, board: StudioBoard): string[] {
    const issues: string[] = [];
    if (!p || typeof p !== 'object') return ['The proposal must be an object with reply, ops and render.'];
    if (!String(p.reply || '').trim()) issues.push('reply is empty — tell the vendor what you did.');
    if (!Array.isArray(p.ops)) issues.push('ops must be an array (empty is fine).');
    const ids = new Set((board.shots || []).map((s) => s.id));
    for (const op of p.ops || []) {
        if (op.op === 'add_shot') {
            for (const u of op.shot?.productUuids || []) if (!knownProductUuids.has(u)) issues.push(`add_shot uses product ${u}, which no search returned — search_products first.`);
            if (op.shot?.kind !== 'card' && !op.shot?.productUuids?.length) issues.push('add_shot needs at least one productUuid.');
            if (op.shot?.kind !== 'card' && !String(op.shot?.scene || '').trim() && !op.shot?.framingRef) issues.push('add_shot needs a scene (or a framingRef to copy).');
            if (op.shot?.id) ids.add(op.shot.id);
        }
        if ((op.op === 'update_shot' || op.op === 'remove_shot') && !ids.has(op.id)) issues.push(`${op.op}: no shot with id ${op.id}.`);
        if (op.op === 'update_shot') for (const u of op.patch?.productUuids || []) if (!knownProductUuids.has(u)) issues.push(`update_shot uses product ${u}, which no search returned.`);
    }
    return issues;
}
